import { useState } from 'react';
import { Plus, Pencil, Trash2, BrainCircuit, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import {
  usePromptConfigs,
  useUpsertPromptConfig,
  useDeletePromptConfig,
  useSyncPipelinePrompts,
  usesUserTemplate,
  PIPELINE_PROMPT_TYPES,
} from '@/lib/admin/prompt-configs';
import type {
  PromptConfig,
  PromptConfigType,
  UpsertPromptConfigInput,
} from '@/lib/admin/prompt-configs';

/** Ordre d'affichage des sections de la page. */
const ALL_PROMPT_TYPES: PromptConfigType[] = [
  'import_lesson',
  'solve_path',
  ...PIPELINE_PROMPT_TYPES,
];

const PIPELINE_TYPE_LABEL: Record<(typeof PIPELINE_PROMPT_TYPES)[number], string> = {
  scenarios_generate: 'Pipeline · Génération scénarios',
  scenarios_audit: 'Pipeline · Audit scénarios',
  scenarios_regenerate: 'Pipeline · Régénération scénario',
  matching: 'Pipeline · Matching curriculum',
  statement_generate: 'Pipeline · Génération énoncé',
  statement_verify: 'Pipeline · Vérification énoncé',
  solution_generate: 'Pipeline · Génération corrigé',
  solution_verify: 'Pipeline · Vérification corrigé',
  distractors_generate: 'Pipeline · Génération distracteurs',
};

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const TYPE_LABEL: Record<PromptConfigType, string> = {
  import_lesson: 'Import Leçon',
  solve_path: 'Solve Path',
  ...PIPELINE_TYPE_LABEL,
};

const TYPE_DESC: Record<PromptConfigType, string> = {
  import_lesson: 'Template avec {{subject}}, {{level}}, {{title}}, {{competences}}, {{sources}}',
  solve_path: 'Prompt système envoyé à Claude avant le message utilisateur',
  scenarios_generate: 'Prompt système du pipeline — étape de génération des scénarios',
  scenarios_audit: 'Prompt système du pipeline — étape d’audit des scénarios',
  scenarios_regenerate: 'Prompt système du pipeline — régénération d’un scénario',
  matching: 'Prompt système du pipeline — matching scénario × curriculum',
  statement_generate: 'Prompt système du pipeline — génération de l’énoncé',
  statement_verify: 'Prompt système du pipeline — vérification de l’énoncé',
  solution_generate: 'Prompt système du pipeline — génération du corrigé',
  solution_verify: 'Prompt système du pipeline — vérification du corrigé',
  distractors_generate: 'Prompt système du pipeline — génération des distracteurs',
};

function scopeLabel(config: PromptConfig): string {
  const subject = config.subject ? (SUBJECT_LABEL[config.subject] ?? config.subject) : null;
  const level = config.level ? (LEVEL_LABEL[config.level] ?? config.level) : null;
  if (subject && level) return `${subject} — ${level}`;
  if (subject) return `${subject} — tous niveaux`;
  if (level) return `Toutes matières — ${level}`;
  return 'Global (fallback)';
}

// ── Formulaire création/édition ───────────────────────────────────────────────

type FormState = {
  type: PromptConfigType;
  subject: SubjectSlug | '';
  level: SchoolLevel | '';
  system_prompt: string;
  user_prompt_template: string;
  prompt_version: string;
  is_active: boolean;
};

function initForm(
  config?: PromptConfig,
  defaultType?: PromptConfigType,
  globalDefault?: PromptConfig,
): FormState {
  return {
    type: config?.type ?? defaultType ?? 'import_lesson',
    subject: config?.subject ?? '',
    level: config?.level ?? '',
    system_prompt: config?.system_prompt ?? globalDefault?.system_prompt ?? '',
    user_prompt_template: config?.user_prompt_template ?? globalDefault?.user_prompt_template ?? '',
    prompt_version: config?.prompt_version ?? globalDefault?.prompt_version ?? '1.0.0',
    is_active: config?.is_active ?? true,
  };
}

function PromptEditor({
  config,
  defaultType,
  globalDefault,
  onCancel,
}: {
  config?: PromptConfig;
  defaultType?: PromptConfigType;
  globalDefault?: PromptConfig;
  onCancel: () => void;
}) {
  const upsert = useUpsertPromptConfig();
  const deleteConfig = useDeletePromptConfig();
  const [form, setForm] = useState<FormState>(() => initForm(config, defaultType, globalDefault));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEdit = !!config;
  const promptField = usesUserTemplate(form.type) ? 'user_prompt_template' : 'system_prompt';
  const promptValue = usesUserTemplate(form.type) ? form.user_prompt_template : form.system_prompt;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    const input: UpsertPromptConfigInput = {
      id: config?.id,
      type: form.type,
      subject: form.subject || null,
      level: form.level || null,
      system_prompt: usesUserTemplate(form.type) ? null : form.system_prompt || null,
      user_prompt_template: usesUserTemplate(form.type) ? form.user_prompt_template || null : null,
      prompt_version: form.prompt_version,
      is_active: form.is_active,
    };
    await upsert.mutateAsync(input);
    onCancel();
  }

  async function handleDelete() {
    if (!config) return;
    await deleteConfig.mutateAsync(config.id);
    onCancel();
  }

  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-5">
      <h3 className="font-display text-ink-900 mb-4 text-[15px] font-bold">
        {isEdit ? 'Modifier la config' : 'Nouvelle config'}
      </h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Type */}
        <div>
          <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
            Type
          </label>
          <select
            value={form.type}
            disabled={isEdit}
            onChange={(e) => set('type', e.target.value as PromptConfigType)}
            className={cn(
              'border-ink-200 text-ink-900 w-full rounded-lg border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500',
              isEdit && 'bg-ink-50 cursor-not-allowed opacity-60',
            )}
          >
            {ALL_PROMPT_TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>

        {/* Matière */}
        <div>
          <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
            Matière
          </label>
          <select
            value={form.subject}
            disabled={isEdit}
            onChange={(e) => set('subject', e.target.value as SubjectSlug | '')}
            className={cn(
              'border-ink-200 text-ink-900 w-full rounded-lg border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500',
              isEdit && 'bg-ink-50 cursor-not-allowed opacity-60',
            )}
          >
            <option value="">Toutes matières</option>
            {SUBJECTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Niveau */}
        <div>
          <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
            Niveau
          </label>
          <select
            value={form.level}
            disabled={isEdit}
            onChange={(e) => set('level', e.target.value as SchoolLevel | '')}
            className={cn(
              'border-ink-200 text-ink-900 w-full rounded-lg border px-3 py-2 font-sans text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500',
              isEdit && 'bg-ink-50 cursor-not-allowed opacity-60',
            )}
          >
            <option value="">Tous niveaux</option>
            {SCHOOL_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Prompt content */}
      <div className="mt-4">
        <label className="font-display text-ink-700 mb-1 block text-[12px] font-semibold">
          {usesUserTemplate(form.type) ? 'Template prompt utilisateur' : 'Prompt système'}
        </label>
        <p className="text-ink-400 mb-2 font-sans text-[11px]">{TYPE_DESC[form.type]}</p>
        <textarea
          value={promptValue}
          onChange={(e) => set(promptField, e.target.value)}
          rows={20}
          className="border-ink-200 text-ink-900 w-full rounded-lg border px-3 py-2 font-mono text-[12px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={
            usesUserTemplate(form.type) ? 'Tu es un expert pédagogique...' : 'Tu es Jeraisonne...'
          }
          spellCheck={false}
        />
      </div>

      {/* Version & actif */}
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="font-display text-ink-700 text-[12px] font-semibold">Version</label>
          <input
            type="text"
            value={form.prompt_version}
            onChange={(e) => set('prompt_version', e.target.value)}
            className="border-ink-200 text-ink-900 w-28 rounded-lg border px-2 py-1 font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            className="h-4 w-4 rounded accent-blue-500"
          />
          <span className="font-display text-ink-700 text-[12px] font-semibold">Actif</span>
        </label>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={upsert.isPending}>
            Annuler
          </Button>
        </div>

        {isEdit && (
          <div>
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="font-sans text-[12px] text-red-600">
                  Confirmer la suppression ?
                </span>
                <button
                  onClick={handleDelete}
                  disabled={deleteConfig.isPending}
                  className="font-sans text-[12px] font-semibold text-red-600 underline hover:text-red-800 disabled:opacity-50"
                >
                  Oui, supprimer
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-ink-500 hover:text-ink-700 font-sans text-[12px] underline"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1 font-sans text-[12px] text-red-400 transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            )}
          </div>
        )}
      </div>

      {upsert.error && (
        <p className="mt-2 font-sans text-[12px] text-red-600">
          {upsert.error instanceof Error ? upsert.error.message : "Erreur lors de l'enregistrement"}
        </p>
      )}
    </div>
  );
}

// ── Card config ───────────────────────────────────────────────────────────────

function PromptConfigCard({
  config,
  expanded,
  onToggle,
}: {
  config: PromptConfig;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [showContent, setShowContent] = useState(false);
  const promptText = usesUserTemplate(config.type)
    ? config.user_prompt_template
    : config.system_prompt;

  return (
    <div className="border-ink-200 overflow-hidden rounded-2xl border bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        <div className="bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
          <BrainCircuit className="text-primary-500 h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 text-[14px] font-semibold">
            {scopeLabel(config)}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-ink-500 font-mono text-[11px]">v{config.prompt_version}</span>
            <span className="text-ink-300">·</span>
            <span
              className={cn(
                'font-display rounded-full px-2 py-0.5 text-[10px] font-semibold',
                config.is_active ? 'bg-xp-100 text-xp-700' : 'bg-ink-100 text-ink-500',
              )}
            >
              {config.is_active ? 'Actif' : 'Inactif'}
            </span>
            {promptText && (
              <>
                <span className="text-ink-300">·</span>
                <span className="text-ink-400 font-sans text-[11px]">
                  {promptText.length.toLocaleString()} caractères
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setShowContent((p) => !p)}
            className="text-ink-400 hover:text-ink-600 font-sans text-[11px] transition-colors"
            title={showContent ? 'Masquer le prompt' : 'Aperçu'}
          >
            {showContent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={onToggle}
            className="text-primary-600 hover:text-primary-800 flex items-center gap-1 font-sans text-[12px] font-semibold transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Modifier
          </button>
        </div>
      </div>

      {/* Aperçu du prompt */}
      {showContent && promptText && (
        <div className="border-ink-100 border-t px-4 pb-4 pt-3">
          <pre className="text-ink-600 bg-ink-50 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg p-3 font-mono text-[11px] leading-relaxed">
            {promptText.slice(0, 2000)}
            {promptText.length > 2000 && '\n…'}
          </pre>
        </div>
      )}

      {/* Éditeur inline */}
      {expanded && (
        <div className="border-ink-100 border-t p-4">
          <PromptEditor config={config} onCancel={onToggle} />
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export function TutorAdminPromptsPage() {
  const { data: configs = [], isLoading } = usePromptConfigs();
  const syncPipeline = useSyncPipelinePrompts();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creatingType, setCreatingType] = useState<PromptConfigType | null>(null);

  const byType = (type: PromptConfigType) => configs.filter((c) => c.type === type);

  function toggleEdit(id: string) {
    setCreatingType(null);
    setEditingId((prev) => (prev === id ? null : id));
  }

  function startCreate(type: PromptConfigType) {
    setEditingId(null);
    setCreatingType((prev) => (prev === type ? null : type));
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Prompts IA</h1>
        <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
          Prompts envoyés à Claude : import de leçons, solve paths, et les 9 étapes du pipeline de
          génération d'exercices. La config la plus précise (matière + niveau) est prioritaire sur
          le fallback global.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => syncPipeline.mutate()}
            disabled={syncPipeline.isPending}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {syncPipeline.isPending ? 'Initialisation…' : 'Initialiser les prompts du pipeline'}
          </Button>
          {syncPipeline.isSuccess && (
            <span className="font-sans text-[12px] text-emerald-600">
              {syncPipeline.data.count} prompts synchronisés.
            </span>
          )}
          {syncPipeline.isError && (
            <span className="font-sans text-[12px] text-red-600">
              {syncPipeline.error instanceof Error
                ? syncPipeline.error.message
                : 'Erreur de synchronisation'}
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-8">
          {ALL_PROMPT_TYPES.map((type) => {
            const typeConfigs = byType(type);
            return (
              <section key={type}>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="font-display text-ink-800 text-[16px] font-bold">
                      {TYPE_LABEL[type]}
                    </h2>
                    <p className="text-ink-400 font-sans text-[11px]">{TYPE_DESC[type]}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => startCreate(type)}
                    className="flex shrink-0 items-center gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Ajouter</span>
                  </Button>
                </div>

                {/* Formulaire de création — pré-rempli avec le prompt global */}
                {creatingType === type && (
                  <div className="mb-3">
                    <PromptEditor
                      defaultType={type}
                      globalDefault={typeConfigs.find(
                        (c) => c.subject === null && c.level === null,
                      )}
                      onCancel={() => setCreatingType(null)}
                    />
                  </div>
                )}

                {typeConfigs.length === 0 && creatingType !== type ? (
                  <div className="border-ink-100 rounded-2xl border border-dashed py-8 text-center">
                    <p className="text-ink-400 font-sans text-[13px]">Aucune config</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {typeConfigs.map((config) => (
                      <PromptConfigCard
                        key={config.id}
                        config={config}
                        expanded={editingId === config.id}
                        onToggle={() => toggleEdit(config.id)}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
